import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';

interface MobileCardProps {
  title: string;
  children: React.ReactNode;
  fullWidth?: boolean;
}

export const MobileCard: React.FC<MobileCardProps> = ({ title, children, fullWidth = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Card 
      sx={{ 
        mb: 2,
        ...(isMobile && {
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }),
      }}
    >
      <CardContent sx={{ p: isMobile ? 2 : 3 }}>
        <Typography 
          variant={isMobile ? "h6" : "h5"} 
          component="h2" 
          gutterBottom
          sx={{ 
            fontWeight: 'bold',
            color: theme.palette.primary.main,
          }}
        >
          {title}
        </Typography>
        {children}
      </CardContent>
    </Card>
  );
};

interface MobileGridProps {
  children: React.ReactNode;
}

export const MobileGrid: React.FC<MobileGridProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Grid container spacing={isMobile ? 2 : 3}>
      {children}
    </Grid>
  );
};

// Composant Grid simplifié pour éviter les erreurs TypeScript
export const MobileGridItem: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Box sx={{ width: '100%', mb: 2 }}>
      {children}
    </Box>
  );
};

// Hook pour détecter si on est sur mobile
export const useIsMobile = () => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down('md'));
};

// Composant pour les boutons d'action flottants sur mobile
interface MobileFloatingActionProps {
  children: React.ReactNode;
}

export const MobileFloatingAction: React.FC<MobileFloatingActionProps> = ({ children }) => {
  const isMobile = useIsMobile();

  if (!isMobile) return <>{children}</>;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 1000,
      }}
    >
      {children}
    </Box>
  );
};

// Composant pour les en-têtes de page optimisés mobile
interface MobilePageHeaderProps {
  title: string;
  subtitle?: string;
}

export const MobilePageHeader: React.FC<MobilePageHeaderProps> = ({ title, subtitle }) => {
  const isMobile = useIsMobile();
  const theme = useTheme();

  return (
    <Box sx={{ mb: isMobile ? 2 : 3 }}>
      <Typography 
        variant={isMobile ? "h4" : "h3"} 
        component="h1" 
        gutterBottom
        sx={{ 
          fontWeight: 'bold',
          color: theme.palette.primary.main,
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography 
          variant={isMobile ? "body1" : "h6"} 
          color="text.secondary"
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
};
